import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'hooks/useTranslation';
import { useCaptureIntersections } from 'hooks/useCaptureIntersections';
import Select from 'react-select';
import Icon from 'components/ui/Icon';
import { Direction } from 'types/Utilities';
import { formatNumber } from 'utils/number';
import QdModal from 'components/ui/QdModal';
import clsx from 'clsx';
import FilterContext from './filterContext';
import ReviewsList from './ReviewList';
import styles from './Reviews.module.scss';
import ReviewStats from './ReviewStats';
import type { ReactNode } from 'react';
import type { ReviewItem } from 'types/Review';
import type { ReviewStats as ReviewStatsType } from 'types/Review';

type Props = {
  reviews?: ReviewItem[];
  sideContent?: ReactNode | null;
  stats?: ReviewStatsType;
  statsOnly?: boolean;
  title?: string;
  noGrid?: boolean;
};

type SelectType = { label: string; value: string };

const filterReviews = (
  isAllReviewsModalOpen: boolean,
  reviews: ReviewItem[] | undefined,
  filter: SelectType,
): ReviewItem[] | undefined => {
  if (!isAllReviewsModalOpen) {
    const filteredResult = [...(reviews || [])]?.sort((a, b) => {
      const [overalRatingOfA, overalRatingOfB] = [a, b].map((review) =>
        review.overallRating === 5 ? 4 : review.overallRating,
      );
      const overalRatingDifference =
        (overalRatingOfA as number) - (overalRatingOfB as number);

      if (
        overalRatingDifference > 0 ||
        (a.date as string) > (b.date as string)
      ) {
        return -1;
      }

      if (
        overalRatingDifference < 0 ||
        (a.date as string) < (b.date as string)
      ) {
        return 1;
      }

      return overalRatingDifference;
    });
    return filteredResult;
  }

  if (!isNaN(+filter.value)) {
    const filteredResult = reviews?.filter((review) => {
      return review.overallRating === +filter.value;
    });
    return filteredResult;
  }

  if (filter.value === 'all') {
    return reviews;
  }

  const filteredResult = reviews?.filter(
    (review) =>
      review.destination?.items.some(
        (destinationItem) =>
          destinationItem &&
          'slug' in destinationItem &&
          destinationItem?.slug === filter.value,
      ),
  );
  return filteredResult;
};

const Reviews = ({
  reviews,
  title,
  stats,
  statsOnly,
  noGrid,
}: Props): JSX.Element | null => {
  const { t, locale } = useTranslation();

  const hasStats = stats?.total && stats?.total > 0;
  const reviewSectionRef = useRef<HTMLDivElement | null>(null);
  useCaptureIntersections(reviewSectionRef);

  const starsShown = reviews?.reduce((acc, val) => {
    const overallRating = val.overallRating?.toString();
    const isExisted = acc.find((review) => review.value === overallRating);
    if (!isExisted && overallRating) {
      acc.push({
        value: overallRating,
        label: `${overallRating} ${t('reviews.star')}`,
      });
    }

    return acc;
  }, [] as SelectType[]);

  const [filterOptions, setFilterOptions] = useState([
    {
      value: 'all',
      label: t('reviews.allReviews'),
    },
  ]);

  const [filter, setFilter] = useState(filterOptions[0]);
  const [isAllReviewsModalOpen, setIsAllReviewsModalOpen] = useState(false);

  const modalRef = useRef<HTMLDivElement | null>(null);
  const [selectedCardIndex, setSelectedCardIndex] = useState(-1);

  useEffect(() => {
    if (selectedCardIndex === -1 || !isAllReviewsModalOpen) {
      return;
    }

    const comments =
      modalRef.current?.children[2].children[0].children[1].children[0];
    const selectedComment = comments?.children[selectedCardIndex];

    selectedComment?.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    });
  }, [isAllReviewsModalOpen, selectedCardIndex]);

  useEffect(() => {
    const filterOptionsByTreatmentName = reviews?.reduce((acc, val) => {
      const treatment = val.destination?.items.find(
        (item) => item && item.__typename === 'TreatmentPage',
      );

      if (
        treatment &&
        treatment?.__typename === 'TreatmentPage' &&
        treatment.title &&
        treatment.slug
      ) {
        const isExisted = acc.find((item) => item.value === treatment.slug);
        if (!isExisted) {
          acc.push({
            label: treatment.title,
            value: treatment.slug,
          });
        }
      }

      return acc;
    }, [] as SelectType[]);

    if (filterOptionsByTreatmentName && starsShown) {
      setFilterOptions([
        ...filterOptions,
        ...starsShown.sort(sortByNameDesc),
        ...filterOptionsByTreatmentName.sort(sortByNameAsc),
      ]);
    }
  }, []);

  const sortByNameAsc = (a: SelectType, b: SelectType): number => {
    return a.label.localeCompare(b.label);
  };

  const sortByNameDesc = (a: SelectType, b: SelectType): number => {
    return b.label.localeCompare(a.label);
  };

  const handleCloseAllReviewsModal = (): void => {
    setIsAllReviewsModalOpen(false);
    setFilter(filterOptions[0]);
    document.body.classList.remove('overflow-hidden');
    setSelectedCardIndex(-1);
  };

  const handleOpenAllReviewsModal = (): void => {
    setIsAllReviewsModalOpen(true);
  };

  const filteredReviews = filterReviews(isAllReviewsModalOpen, reviews, filter);
  return (
    <>
      {isAllReviewsModalOpen && (
        <QdModal
          fullScreen
          backdropBlur
          className={styles.reviewsModal}
          onClose={() => {
            handleCloseAllReviewsModal();
          }}
          open={isAllReviewsModalOpen}
          title={' '}
          showPrevIcon={false}
          showCloseIcon={true}
          showCloseButton={false}
          ref={modalRef}
        >
          <div
            className={clsx(styles.reviews, styles.noGrid)}
            ref={reviewSectionRef}
          >
            <div className={clsx(styles.titleWrapper, styles.insideModal)}>
              <div className={styles.title}>
                <h2 className="adelle-bold-section-headline">
                  {title || t('reviews')}
                </h2>
                {hasStats && (
                  <span>
                    {formatNumber(
                      stats?.avg_rating?.toFixed(1) as string,
                      locale,
                    )}
                  </span>
                )}
              </div>
              <div>
                <Select
                  styles={{
                    menu: (css) => ({
                      ...css,
                      width: 'max-content',
                      position: 'absolute',
                      right: 0,
                    }),
                  }}
                  isSearchable={false}
                  name="treatmentObject"
                  options={filterOptions || []}
                  onChange={(option) => {
                    if (option) {
                      setFilter(option);
                    }
                  }}
                  defaultValue={filterOptions[0]}
                />
              </div>
            </div>
            <div className={styles.reviewsWrapper}>
              {filteredReviews && (
                <FilterContext.Provider value={{ filter, setFilter }}>
                  <ReviewsList reviews={filteredReviews} isShownAll />
                </FilterContext.Provider>
              )}
            </div>
          </div>
        </QdModal>
      )}

      <div
        className={clsx(
          styles.reviews,
          !noGrid ? styles.reviewsGrid : styles.noGrid,
        )}
        ref={reviewSectionRef}
      >
        <div className={styles.titleWrapper}>
          <div className={styles.title}>
            <h2 className="adelle-bold-section-headline">
              {title || t('reviews')}
            </h2>
            {!statsOnly && hasStats && (
              <span>
                {formatNumber(stats?.avg_rating?.toFixed(1) as string, locale)}
              </span>
            )}
          </div>

          {!statsOnly && (
            <div>
              <button
                className={styles.button}
                onClick={handleOpenAllReviewsModal}
              >
                <span>{t('reviews.allReviews')}</span>
                <Icon type="Angle" direction={Direction.RIGHT} className="" />
              </button>
            </div>
          )}
        </div>

        <div className={styles.reviewsContainer}>
          {stats && statsOnly && <ReviewStats stats={stats} />}
          {!statsOnly && filteredReviews ? (
            <FilterContext.Provider value={{ filter, setFilter }}>
              <ReviewsList
                reviews={filteredReviews}
                onReviewCardClick={handleOpenAllReviewsModal}
                setSelectedCardIndex={setSelectedCardIndex}
              />
            </FilterContext.Provider>
          ) : null}
        </div>
      </div>
    </>
  );
};

export default Reviews;
